import React, { useState } from 'react';
import { toast } from 'react-toastify';
import BudgetItemAllocation from './BudgetItemAllocation';

function BudgetsTable({ budgetsData, selectedYear, onEditBudget, onCreateBudget, onRefresh }) {
  const { budget = {}, summary = {} } = budgetsData;
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('glCode');
  const [sortOrder, setSortOrder] = useState('asc');
  const [allocationModal, setAllocationModal] = useState({ isOpen: false, budgetId: null, budgetAmount: 0, monthName: '' });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount || 0);
  };

  const getMonthName = (monthNum) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[parseInt(monthNum) - 1] || monthNum;
  };

  // Render All Years View (Legacy Style)
  const renderAllYearsView = () => {
    if (Object.keys(budget).length === 0) {
      return <div className="text-center py-8 text-gray-500">No budget data available.</div>;
    }

    return (
      <div className="space-y-8">
        {Object.entries(budget).map(([glCode, glData]) => (
          <div key={glCode} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
             {/* Iterate through years for this GL Code */}
             {Object.entries(glData.year || {}).sort((a, b) => b[0] - a[0]).map(([year, yearData]) => {
                const yearTotal = summary[year]?.[glCode]?.amount || 0;
                
                return (
                  <div key={year} className="border-b border-gray-200 last:border-b-0">
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase">GL Code</p>
                          <p className="text-lg font-bold text-gray-900">{glCode}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase">Description</p>
                          <p className="text-lg font-medium text-gray-900">{glData.description}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase">Total</p>
                          <p className="text-lg font-bold text-blue-600">{formatCurrency(yearTotal)}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase">Year</p>
                          <p className="text-lg font-bold text-gray-900">{year}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="divide-y divide-gray-200">
                        <thead className="bg-blue-50">
                          <tr>
                            {Object.keys(yearData).sort((a,b) => a-b).map(month => (
                              <th key={month} className="px-2 py-3 text-left text-xs font-medium text-blue-800 uppercase tracking-wider">
                                {getMonthName(month)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          <tr>
                            {Object.entries(yearData).sort((a,b) => a[0]-b[0]).map(([month, monthData]) => {
                              // Calculate total allocated to items
                              const itemsTotal = monthData.items?.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0) || 0;
                              const hasAllocations = itemsTotal > 0;
                              const unallocated = (parseFloat(monthData.amount) || 0) - itemsTotal;
                              
                              return (
                              <td key={month} className="px-2 py-4 align-top">
                                <div className="space-y-1">
                                  <div className="px-1 py-1 bg-green-50 text-green-700 font-bold rounded text-center border border-green-100 text-xs">
                                    {formatCurrency(monthData.amount)}
                                  </div>
                                  
                                  {monthData.items && monthData.items.length > 0 && (
                                    <ul className="space-y-1">
                                      {monthData.items.map((item, idx) => (
                                        <li key={idx} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-100">
                                          <div className="flex justify-between items-center">
                                            <span className="truncate flex-1 mr-1">{item.name}</span>
                                            {parseFloat(item.amount) > 0 && (
                                              <span className="text-blue-600 font-medium whitespace-nowrap">
                                                {formatCurrency(item.amount)}
                                              </span>
                                            )}
                                          </div>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                  
                                  {/* Show unallocated amount warning */}
                                  {monthData.items && monthData.items.length > 0 && unallocated > 0 && (
                                    <div className="px-1 py-0.5 bg-yellow-50 text-yellow-700 text-[10px] rounded border border-yellow-200 text-center">
                                      Unallocated: {formatCurrency(unallocated)}
                                    </div>
                                  )}
                                  
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() => onEditBudget?.(monthData.budget_id)}
                                      className="flex-1 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
                                    >
                                      Edit
                                    </button>
                                    {monthData.items && monthData.items.length > 0 && (
                                      <button
                                        onClick={() => setAllocationModal({
                                          isOpen: true,
                                          budgetId: monthData.budget_id,
                                          budgetAmount: monthData.amount,
                                          monthName: getMonthName(month)
                                        })}
                                        className="flex-1 py-1 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded transition-colors"
                                        title="Allocate amounts to items"
                                      >
                                        Allocate
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </td>
                              );
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
             })}
          </div>
        ))}
      </div>
    );
  };

  if (!selectedYear) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center px-1">
           <h2 className="text-xl font-bold text-gray-800">Detailed Budget View (All Years)</h2>
        </div>
        {renderAllYearsView()}
      </div>
    );
  }

  // Filter budget items based on search term
  const filteredBudgetEntries = Object.entries(budget).filter(([glCode, glData]) => {
    if (!searchTerm) return true;
    return (
      glCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      glData.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  // Calculate totals for the footer
  let totalAmount = 0;
  let entryCount = 0;

  Object.values(budget).forEach(glData => {
      const yearData = glData.year?.[selectedYear] || {};
      Object.values(yearData).forEach(monthData => {
          totalAmount += parseFloat(monthData.amount || 0);
          entryCount++;
      });
  });

  // Render Single Year View (Horizontal Layout)
  const renderSingleYearView = () => {
    if (Object.keys(budget).length === 0) {
      return (
        <div className="p-8 text-center text-gray-500 border border-gray-200 rounded-lg bg-white">
          {searchTerm ? 'No budgets found matching your search criteria.' : `No budget data available for ${selectedYear}.`}
        </div>
      );
    }

    if (filteredBudgetEntries.length === 0) {
       return (
        <div className="p-8 text-center text-gray-500 border border-gray-200 rounded-lg bg-white">
          No budgets found matching your search criteria.
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {filteredBudgetEntries.map(([glCode, glData]) => {
          const yearData = glData.year?.[selectedYear] || {};
          const yearTotal = summary[selectedYear]?.[glCode]?.amount || 0;
          
          return (
            <div key={glCode} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">GL Code</p>
                    <p className="text-lg font-bold text-gray-900">{glCode}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase">Description</p>
                    <p className="text-lg font-medium text-gray-900">{glData.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-medium text-gray-500 uppercase">Total for {selectedYear}</p>
                    <p className="text-lg font-bold text-blue-600">{formatCurrency(yearTotal)}</p>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-blue-50">
                    <tr>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                        <th key={month} className="px-2 py-2 text-left text-xs font-medium text-blue-800 uppercase tracking-wider min-w-[120px]">
                          {getMonthName(month)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => {
                        // Map month number to full month name as used in the API response
                        const fullMonthNames = [
                          'January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'
                        ];
                        const monthName = fullMonthNames[month - 1];
                        
                        // Handle various key formats (Full Name, number, string, padded string)
                        const monthData = yearData[monthName] || yearData[month] || yearData[String(month)] || yearData[String(month).padStart(2, '0')];
                        
                        // Calculate item allocations
                        const itemsTotal = monthData?.items?.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0) || 0;
                        const unallocated = monthData ? (parseFloat(monthData.amount) || 0) - itemsTotal : 0;
                        
                        return (
                          <td key={month} className="px-2 py-2 align-top border-r border-gray-100 last:border-r-0">
                            {monthData ? (
                              <div className="space-y-1 w-full min-w-[100px]">
                                <div className="w-full p-1 bg-green-50 text-green-700 font-bold rounded text-center border border-green-100 text-xs">
                                  {formatCurrency(monthData.amount)}
                                </div>
                                
                                {monthData.items && monthData.items.length > 0 && (
                                  <ul className="w-full space-y-0.5 list-none p-0 m-0">
                                    {monthData.items.map((item, idx) => (
                                      <li key={idx} className="w-full block text-[10px] text-gray-600 bg-gray-50 px-1 py-0.5 rounded border border-gray-100">
                                        <div className="flex justify-between items-center">
                                          <span className="truncate flex-1">{item.name}</span>
                                          {parseFloat(item.amount) > 0 && (
                                            <span className="text-blue-600 font-medium ml-1 whitespace-nowrap">
                                              {formatCurrency(item.amount)}
                                            </span>
                                          )}
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                
                                {/* Show unallocated amount warning */}
                                {monthData.items && monthData.items.length > 0 && unallocated > 0 && (
                                  <div className="px-1 py-0.5 bg-yellow-50 text-yellow-700 text-[9px] rounded border border-yellow-200 text-center">
                                    Unallocated: {formatCurrency(unallocated)}
                                  </div>
                                )}
                                
                                <div className="flex space-x-1">
                                  <button
                                    onClick={() => onEditBudget?.(monthData.budget_id)}
                                    className="flex-1 py-0.5 text-[10px] font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors text-center"
                                  >
                                    Edit
                                  </button>
                                  {monthData.items && monthData.items.length > 0 && (
                                    <button
                                      onClick={() => setAllocationModal({
                                        isOpen: true,
                                        budgetId: monthData.budget_id,
                                        budgetAmount: monthData.amount,
                                        monthName: getMonthName(month)
                                      })}
                                      className="flex-1 py-0.5 text-[10px] font-medium text-purple-600 hover:text-purple-800 hover:underline transition-colors text-center"
                                      title="Allocate amounts to items"
                                    >
                                      Allocate
                                    </button>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <button
                                  onClick={() => onCreateBudget?.({
                                    year: selectedYear,
                                    month: month,
                                    gl_code: glCode,
                                    description: glData.description
                                  })}
                                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Create budget for this month"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                </button>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="flex-shrink-0 rounded-lg p-2.5 mr-3" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
              <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Budget Table - {selectedYear}</h1>
              <p className="text-xs text-gray-500">{filteredBudgetEntries.length} entries • Total: {formatCurrency(totalAmount)}</p>
            </div>
          </div>

          {/* Action Buttons and Search */}
          <div className="flex items-center space-x-2 ml-4">
            {/* Search Bar */}
            <div className="relative w-[400px]">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Search GL codes or descriptions..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Budget Table (Horizontal Layout) */}
      {renderSingleYearView()}

      {/* Summary Footer */}
      {Object.keys(budget).length > 0 && (
        <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
          <div className="flex justify-between items-center">
            <div>
              <h4 className="text-lg font-medium text-gray-900">Summary</h4>
              <p className="text-sm text-gray-600">Budget overview for {selectedYear}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{formatCurrency(totalAmount)}</div>
              <div className="text-sm text-gray-600">{entryCount} budget entries</div>
            </div>
          </div>
        </div>
      )}

      {/* Budget Item Allocation Modal */}
      <BudgetItemAllocation
        isOpen={allocationModal.isOpen}
        onClose={() => setAllocationModal({ isOpen: false, budgetId: null, budgetAmount: 0, monthName: '' })}
        budgetId={allocationModal.budgetId}
        budgetAmount={allocationModal.budgetAmount}        monthName={allocationModal.monthName}        onSuccess={() => {
          onRefresh?.();
        }}
      />
    </div>
  );
}

export default BudgetsTable;
